<template>
  <master-layout v-model="activeStep" :navigation="steps">
    <template #header>
      <div class="d-flex align-center">
        <div class="caption">Мастер Вариантов размещения</div>
        <v-spacer />
        <cancel-confirmation />
      </div>
    </template>

    <room-categories
      v-if="activeStep === 0"
      v-model="roomCategoriesArr"
      :selected-room-categories="selectedRoomCategories"
      @next="next"
    />
    <tourists
      v-if="activeStep === 1"
      v-model="touristsData"
      :is-loading="isLoading"
      @back="back"
      @next="generateVariants"
    />
    <placing-variants
      v-if="activeStep === 2 && PVMPlacingVariants"
      :placing-variants="PVMPlacingVariants"
      :tourist-types="touristsData.touristTypes"
      :loading="isLoading"
      @next="submit"
      @back="back"
    />
  </master-layout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type {
  PVMPlacingVariantsItem,
  PVMPlacingVariants as PVMPlacingVariantsType,
  PVMTouristsInfo,
} from '@/parts/tariffs/types/tariffs';
import MasterLayout from '@/core/layouts/components/master/MasterLayout.vue';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { useTourStore } from '@/core/store/tour';
import CancelConfirmation from '@/parts/tariffs/components/PVM/CancelConfirmation.vue';
import PlacingVariants from '@/parts/tariffs/components/PVM/PlacingVariants.vue';
import RoomCategories from '@/parts/tariffs/components/PVM/RoomCategories.vue';
import Tourists from '@/parts/tariffs/components/PVM/TouristTypes.vue';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const $router = useRouter();
const $route = useRoute();

const {
  PVMMaterData,
  PvmGenerate,
  submitPVM,
  activeTariff,
  getActiveTariff,
  startPlacingVariantsMaster,
  cancelPVMEdit,
} = useTariffsStore();

onBeforeUnmount(() => {
  cancelPVMEdit();
});

await getActiveTariff(String($route.params.t_id));

const result = await startPlacingVariantsMaster();

if (result?.errors) {
  await $router.replace({
    name: RouteNames.TariffSettings,
    params: {
      id: $route.params.id,
      t_id: $route.params.t_id,
    },
  });
}

const activeStep = ref(0);
const availableStep = ref(activeTariff.value?.baseSettings?.completed ? 2 : 0);

const selectedRoomCategories = ref(PVMMaterData.value?.settings?.roomCategories || []);
const roomCategoriesArr = ref(PVMMaterData.value?.settings?.roomCategories.map((rc) => rc.roomCategoryCid) || []);

const touristsData = ref<PVMTouristsInfo>({
  ageRules: PVMMaterData.value?.settings?.ageSettings?.determineAgeMethod || null,
  minAge: PVMMaterData.value?.settings?.ageSettings?.initialAge ?? null,
  ageWithNoPlace: PVMMaterData.value?.settings?.ageSettings?.noSeatAge || null,
  ageWithNoPlaceEnabled: !!PVMMaterData.value?.settings?.ageSettings?.noSeatAge,
  touristTypes:
    PVMMaterData.value?.settings?.ageSettings?.categories?.values?.map((value) => ({
      ...value,
      id: Math.random(),
    })) || [],
});

const PVMPlacingVariants = ref<PVMPlacingVariantsType>();

const mapPlacingVariantMasterData = () => ({
  roomCategoryCids: roomCategoriesArr.value,
  ageSettings: {
    initialAge: touristsData.value.minAge,
    determineAgeMethod: touristsData.value.ageRules!,
    noSeatAge: touristsData.value.ageWithNoPlace,
    categories: {
      values: touristsData.value.touristTypes.map((touristType) => ({
        from: touristType.from,
        to: touristType.to,
      })),
    },
  },
});

const isLoading = ref(false);

const generateVariants = async () => {
  if (!activeTariff.value) {
    return false;
  }

  isLoading.value = true;

  const result = await PvmGenerate({
    tariffScheduleCid: activeTariff.value?.tariffScheduleCid,
    placingVariantsMasterData: mapPlacingVariantMasterData(),
  });

  if (result?.value?.data) {
    PVMPlacingVariants.value = result.value.data.placingVariantsMasterGenerate;
    activeStep.value += 1;
    availableStep.value += 1;
  }

  isLoading.value = false;

  return true;
};

const next = async () => {
  if (activeStep.value === 0) {
    activeStep.value++;
    availableStep.value++;
  }
};

const back = () => {
  activeStep.value -= 1;
};

const { setActiveTour } = useTourStore();

const submit = async (placingVariants: PVMPlacingVariantsItem[]) => {
  if (!activeTariff.value) {
    return;
  }

  isLoading.value = true;

  const result = await submitPVM({
    tariffScheduleCid: activeTariff.value?.tariffScheduleCid,
    placingVariantsMasterData: mapPlacingVariantMasterData(),
    roomPlacingVariants: placingVariants.map((placingVariant) => ({
      roomCategoryCid: placingVariant.roomCid,
      variants: placingVariant.selected.map((selectedPlacingVariant) => {
        return {
          roomPlacingCID: selectedPlacingVariant.tarification!.roomPlacingCid || null,
          placings: selectedPlacingVariant.tarification!.roomPlacingCid
            ? null
            : {
                baseSeats: selectedPlacingVariant.placingVariant.baseSeats.map((s) => ({
                  seats: s.seats,
                  touristType: {
                    ageCategory: s.touristType.ageCategory
                      ? {
                          from: s.touristType.ageCategory.from,
                          to: s.touristType.ageCategory.to,
                        }
                      : null,
                  },
                })),
                extSeats: selectedPlacingVariant.placingVariant.extSeats.map((s) => ({
                  seats: s.seats,
                  touristType: {
                    ageCategory: s.touristType.ageCategory
                      ? {
                          from: s.touristType.ageCategory.from,
                          to: s.touristType.ageCategory.to,
                        }
                      : null,
                  },
                })),
              },
          shortName: selectedPlacingVariant.name,
          defaultAvailable: selectedPlacingVariant.tarification!.defaultAvailable,
          pricePerRoom: selectedPlacingVariant.tarification!.pricePerRoom,
          isNeedConfirm: selectedPlacingVariant.tarification!.isNeedConfirm,
          maxNoSeatQuantity: selectedPlacingVariant.tarification!.maxNoSeatQuantity,
        };
      }),
    })),
  });

  if (!result?.errors) {
    setActiveTour('tariffPlacingVariantsTour');

    await $router.push({
      name: RouteNames.TariffSettings,
    });
  }

  isLoading.value = false;
};

const steps = computed(() => [
  {
    title: 'Категории номеров',
  },
  {
    title: 'Информация о туристах',
    disabled: availableStep.value < 1,
  },
  {
    title: 'Варианты размещения',
    disabled: availableStep.value < 2,
    callback: generateVariants,
  },
]);
</script>

<style scoped lang="scss"></style>
